import React, { Suspense } from 'react'
import { Canvas } from '@react-three/fiber'
import { Physics, usePlane, useBox } from '@react-three/cannon'
import { Sky, Environment, Loader } from '@react-three/drei'
import { Track } from './models/Track'
import { Vehicle } from './models/Vehicle'
import { Overlay } from './ui/Overlay'
import { Speed } from './ui/Speed'
import { Controls } from './ui/Controls'
import { useStore } from './utils/store'
import Handsfree from 'handsfree'
import 'handsfree/build/lib/assets/handsfree.css'

export function App() {
  const vehicleStart = useStore((state) => state.constants.vehicleStart)
  return (
    <Overlay>
      <Canvas dpr={[1, 1.5]} shadows camera={{ position: [0, 5, 15], fov: 50 }}>
        <fog attach="fog" args={['white', 0, 500]} />
        <Sky sunPosition={[100, 10, 100]} scale={1000} />
        <ambientLight intensity={0.1} />
        <Physics broadphase="SAP" contactEquationRelaxation={4} friction={1e-3} allowSleep>
          <Plane rotation={[-Math.PI / 2, 0, 0]} userData={{ id: 'floor' }} />
          <Vehicle {...vehicleStart} />
          <Ramp position={[120, -1, -50]} />
        </Physics>
        <Track position={[80, 0, -210]} scale={26} />
        <Environment preset="night" />
      </Canvas>
      <Controls />
      <Speed />
    </Overlay>
  )
}

function Plane(props) {
  const [ref] = usePlane(() => ({ type: 'Static', material: 'ground', ...props }))
  return null
}

function Ramp({ args = [10, 2.5, 3], rotation = [0, 0.45, Math.PI / 11], ...props }) {
  const [ref] = useBox(() => ({ type: 'Static', args, rotation, ...props }))
  return (
    <mesh ref={ref}>
      <boxGeometry args={args} />
      <meshStandardMaterial color="indianred" />
    </mesh>
  )
}

/**
 * Configure Handsfree.js
 */
window.handsfree = new Handsfree({
  hands: true,
  assetsPath: '/handsfree/assets',
  showDebug: true
})

/**
 * Turn left/right and accel/deccel
 */
handsfree.use('accelerateBrake', ({hands}) => {
  if (!hands?.gesture) return

  // Accelerate
  if (hands.gesture[1]?.name === 'go') {
    window.dispatchEvent(new KeyboardEvent("keydown", {
      bubbles : true,
      cancelable : true,
      char : "W",
      key : "w",
      keyCode : 87
    }))
  } else {
    window.dispatchEvent(new KeyboardEvent("keyup", {
      bubbles : true,
      cancelable : true,
      char : "W",
      key : "w",
      keyCode : 87
    }))
  }

  // Decelerate
  if (hands.gesture[1]?.name === 'stop') {
    window.dispatchEvent(new KeyboardEvent("keydown", {
      bubbles : true,
      cancelable : true,
      char : "S",
      key : "s",
      keyCode : 83
    }))
  } else {
    window.dispatchEvent(new KeyboardEvent("keyup", {
      bubbles : true,
      cancelable : true,
      char : "S",
      key : "s",
      keyCode : 83
    }))
  }

  // Turn right
  if (hands.gesture[0]?.name === 'right') {
    window.dispatchEvent(new KeyboardEvent("keydown", {
      bubbles : true,
      cancelable : true,
      char : "D",
      key : "d",
      keyCode : 68
    }))
  } else {
    window.dispatchEvent(new KeyboardEvent("keyup", {
      bubbles : true,
      cancelable : true,
      char : "D",
      key : "d",
      keyCode : 68
    }))
  }

  // Turn left
  if (hands.gesture[0]?.name === 'left') {
    window.dispatchEvent(new KeyboardEvent("keydown", {
      bubbles : true,
      cancelable : true,
      char : "A",
      key : "a",
      keyCode : 65
    }))
  } else {
    window.dispatchEvent(new KeyboardEvent("keyup", {
      bubbles : true,
      cancelable : true,
      char : "A",
      key : "a",
      keyCode : 65
    }))
  }
})

/**
 * Stop Gesture
 * @see https://handsfree.dev/tools/gesture-mapper/
 */
handsfree.useGesture({
  "name": "stop",
  "algorithm": "fingerpose",
  "models": "hands",
  "confidence": 7.5,
  "enabled": true,
  "description": [["addCurl","Thumb","NoCurl",1],["addDirection","Thumb","DiagonalUpLeft",1],["addCurl","Index","NoCurl",1],["addDirection","Index","VerticalUp",1],["addDirection","Index","DiagonalUpLeft",0.875],["addCurl","Middle","NoCurl",1],["addDirection","Middle","VerticalUp",1],["addDirection","Middle","DiagonalUpLeft",0.11538461538461539],["addDirection","Middle","DiagonalUpRight",0.038461538461538464],["addCurl","Ring","NoCurl",1],["addDirection","Ring","VerticalUp",1],["addDirection","Ring","DiagonalUpRight",0.2],["addCurl","Pinky","NoCurl",1],["addDirection","Pinky","VerticalUp",1],["addDirection","Pinky","DiagonalUpRight",0.875],["addDirection","Thumb","DiagonalUpRight",1],["addDirection","Index","DiagonalUpRight",0.875],["addDirection","Middle","DiagonalUpRight",0.11538461538461539],["addDirection","Middle","DiagonalUpLeft",0.038461538461538464],["addDirection","Ring","DiagonalUpLeft",0.2],["addDirection","Pinky","DiagonalUpLeft",0.875]]
})

/**
 * Go Gesture
 */
 handsfree.useGesture({
  "name": "go",
  "algorithm": "fingerpose",
  "models": "hands",
  "confidence": 7.5,
  "enabled": true,
  "description": [["addCurl","Thumb","HalfCurl",0.25],["addCurl","Thumb","FullCurl",1],["addDirection","Thumb","HorizontalRight",0.09090909090909091],["addDirection","Thumb","DiagonalUpRight",0.2727272727272727],["addDirection","Thumb","VerticalUp",0.9090909090909091],["addDirection","Thumb","DiagonalUpLeft",1],["addCurl","Index","FullCurl",1],["addDirection","Index","DiagonalUpLeft",1],["addDirection","Index","VerticalUp",0.23529411764705882],["addDirection","Index","HorizontalLeft",0.23529411764705882],["addCurl","Middle","FullCurl",1],["addDirection","Middle","VerticalUp",0.5625],["addDirection","Middle","DiagonalUpLeft",1],["addCurl","Ring","FullCurl",1],["addDirection","Ring","VerticalUp",1],["addDirection","Ring","DiagonalUpLeft",0.4666666666666667],["addDirection","Ring","DiagonalUpRight",0.2],["addCurl","Pinky","FullCurl",1],["addDirection","Pinky","DiagonalUpRight",0.7857142857142857],["addDirection","Pinky","VerticalUp",1],["addDirection","Thumb","HorizontalLeft",0.09090909090909091],["addDirection","Thumb","DiagonalUpLeft",0.2727272727272727],["addDirection","Thumb","DiagonalUpRight",1],["addDirection","Index","DiagonalUpRight",1],["addDirection","Index","HorizontalRight",0.23529411764705882],["addDirection","Middle","DiagonalUpRight",1],["addDirection","Ring","DiagonalUpRight",0.4666666666666667],["addDirection","Ring","DiagonalUpLeft",0.2],["addDirection","Pinky","DiagonalUpLeft",0.7857142857142857]]
})

/**
 * Turn Left
 */
 handsfree.useGesture({
  "name": "left",
  "algorithm": "fingerpose",
  "models": "hands",
  "confidence": 7.5,
  "description": [["addCurl","Thumb","NoCurl",1],["addDirection","Thumb","VerticalUp",1],["addDirection","Thumb","DiagonalUpLeft",0.13043478260869565],["addDirection","Thumb","DiagonalUpRight",0.17391304347826086],["addCurl","Index","FullCurl",1],["addCurl","Index","NoCurl",0.07142857142857142],["addDirection","Index","DiagonalUpLeft",1],["addDirection","Index","VerticalUp",0.1111111111111111],["addCurl","Middle","FullCurl",1],["addDirection","Middle","HorizontalLeft",1],["addDirection","Middle","DiagonalUpLeft",1],["addCurl","Ring","FullCurl",1],["addDirection","Ring","HorizontalLeft",1],["addDirection","Ring","DiagonalUpLeft",0.7647058823529411],["addCurl","Pinky","FullCurl",1],["addDirection","Pinky","HorizontalLeft",1],["addDirection","Pinky","DiagonalDownLeft",0.3333333333333333],["addDirection","Pinky","VerticalDown",0.05555555555555555],["addDirection","Pinky","DiagonalDownRight",0.2777777777777778],["setWeight","Thumb",2]],
  "enabled": true
})

/**
 * Turn Right
 */
 handsfree.useGesture({
  "name": "right",
  "algorithm": "fingerpose",
  "models": "hands",
  "confidence": 7.5,
  "description": [["addCurl","Thumb","NoCurl",1],["addDirection","Thumb","VerticalDown",1],["addDirection","Thumb","DiagonalDownLeft",0.9285714285714286],["addDirection","Thumb","HorizontalLeft",0.07142857142857142],["addCurl","Index","FullCurl",1],["addCurl","Index","HalfCurl",0.037037037037037035],["addDirection","Index","VerticalDown",0.05555555555555555],["addDirection","Index","DiagonalDownLeft",1],["addDirection","Index","HorizontalLeft",0.5],["addCurl","Middle","FullCurl",1],["addCurl","Middle","HalfCurl",0.07692307692307693],["addDirection","Middle","VerticalDown",0.07142857142857142],["addDirection","Middle","DiagonalDownLeft",1],["addDirection","Middle","HorizontalLeft",0.8571428571428571],["addDirection","Middle","DiagonalUpLeft",0.07142857142857142],["addCurl","Ring","FullCurl",1],["addCurl","Ring","HalfCurl",0.07692307692307693],["addDirection","Ring","HorizontalRight",0.058823529411764705],["addDirection","Ring","HorizontalLeft",1],["addDirection","Ring","DiagonalDownLeft",0.11764705882352941],["addDirection","Ring","DiagonalUpLeft",0.47058823529411764],["addCurl","Pinky","FullCurl",1],["addDirection","Pinky","DiagonalUpRight",0.21052631578947367],["addDirection","Pinky","DiagonalUpLeft",1],["addDirection","Pinky","HorizontalLeft",0.2631578947368421],["setWeight","Thumb",2]],
  "enabled": true
})